import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Search, User, Menu, X, Heart, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import Logo from "@/components/Logo";

const links = [
  { to: "/catalogue", label: "Catalogue" },
  { to: "/prescription-info", label: "Prescription" },
  { to: "/shipping", label: "Shipping" },
  { to: "/faq", label: "FAQ" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { items, setOpen } = useCart();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [openMobile, setOpenMobile] = useState(false);
  const totalQty = items.reduce((s, i) => s + i.quantity, 0);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/catalogue?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/90 border-b border-slate-200">
      <div className="blz-container flex items-center h-16 gap-4">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <Logo size="sm" />
        </Link>

        <form onSubmit={submit} className="hidden md:flex flex-1 max-w-xl">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search medicines, brands, compositions..."
              className="w-full h-10 pl-10 pr-4 rounded-full border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:bg-white"
              data-testid="header-search-input"
            />
          </div>
        </form>

        <nav className="hidden lg:flex items-center gap-5 text-sm text-slate-700">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="hover:text-slate-900" data-testid={`nav-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 ml-auto">
          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/wishlist" className="p-2 hover:bg-slate-100 rounded-full" data-testid="wishlist-link">
                <Heart className="h-5 w-5 text-slate-700" />
              </Link>
              <Link to="/account" className="p-2 hover:bg-slate-100 rounded-full" data-testid="account-link">
                <User className="h-5 w-5 text-slate-700" />
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" className="p-2 hover:bg-slate-100 rounded-full" data-testid="admin-link" title="Admin">
                  <ShieldCheck className="h-5 w-5 text-emerald-700" />
                </Link>
              )}
              <button onClick={() => { logout(); nav("/"); }} className="p-2 hover:bg-slate-100 rounded-full" data-testid="logout-btn">
                <LogOut className="h-5 w-5 text-slate-700" />
              </button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/login" className="text-sm text-slate-700 hover:text-slate-900 px-3" data-testid="header-login">Sign in</Link>
              <Link to="/register" className="btn-dark" data-testid="header-register">Create account</Link>
            </div>
          )}

          <button
            onClick={() => setOpen(true)}
            className="relative p-2 rounded-full bg-slate-900 text-white hover:bg-slate-800"
            data-testid="cart-toggle"
            aria-label="Open cart"
          >
            <ShoppingCart className="h-5 w-5" />
            {totalQty > 0 && (
              <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[10px] font-bold h-5 min-w-5 px-1 rounded-full grid place-items-center" data-testid="cart-count">
                {totalQty}
              </span>
            )}
          </button>

          <button
            className="lg:hidden p-2 rounded-full hover:bg-slate-100"
            onClick={() => setOpenMobile(!openMobile)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {openMobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {openMobile && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="blz-container py-4 space-y-3">
            <form onSubmit={submit}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..."
                  className="w-full h-10 pl-10 pr-4 rounded-full border border-slate-200 bg-slate-50 text-sm" />
              </div>
            </form>
            <div className="grid grid-cols-2 gap-2">
              {links.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpenMobile(false)} className="py-2 text-slate-700">{l.label}</Link>
              ))}
              {user ? (
                <>
                  <Link to="/account" onClick={() => setOpenMobile(false)} className="py-2">Account</Link>
                  <Link to="/my-orders" onClick={() => setOpenMobile(false)} className="py-2">My Orders</Link>
                  <Link to="/wishlist" onClick={() => setOpenMobile(false)} className="py-2">Wishlist</Link>
                  {user.role === "admin" && <Link to="/admin" onClick={() => setOpenMobile(false)} className="py-2 text-emerald-700">Admin</Link>}
                  <button onClick={() => { logout(); setOpenMobile(false); }} className="py-2 text-left">Sign out</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpenMobile(false)} className="py-2">Sign in</Link>
                  <Link to="/register" onClick={() => setOpenMobile(false)} className="py-2">Create account</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
